import { type RecoilState, useRecoilState, useResetRecoilState } from 'recoil';
import { useCallback } from 'react';
import {
  type DefaultPagination,
  filterState,
  type FilterWithPaginationStateShape,
  paginationState,
} from './useFilterWithPagination';
import type { TablePaginationShape } from './useTablePagination';

interface FilterWithPaginationStatesOptions<TFilter, TPagination extends DefaultPagination> {
  domainKey: string;
  filterDefaultValue: TFilter;
  paginationDefaultValue?: TPagination;
}

interface FilterWithPaginationStates<TFilter, TPagination extends DefaultPagination> {
  filter: RecoilState<TFilter>;
  pagination: RecoilState<TPagination>;
}

export const filterWithPaginationStates = <TFilter, TPagination extends DefaultPagination = DefaultPagination>({
  domainKey,
  filterDefaultValue,
  paginationDefaultValue,
}: FilterWithPaginationStatesOptions<TFilter, TPagination>): FilterWithPaginationStates<TFilter, TPagination> => ({
  filter: filterState<TFilter>(domainKey, filterDefaultValue),
  pagination: paginationState<TPagination>(domainKey, paginationDefaultValue),
});

const useFilter = <T>(state: RecoilState<T>) => {
  const resetFilterState = useResetRecoilState(state);
  const [filter, setFilterState] = useRecoilState(state);

  return {
    filter,
    setFilterState,
    resetFilterState,
  };
};

export const usePagination = <T extends DefaultPagination>(state: RecoilState<T>, pluralEntityName?: string) => {
  const resetPaginationState = useResetRecoilState(state);
  const [pagination, setPaginationState] = useRecoilState(state);

  return {
    pagination: {
      ...pagination,
      onChange: useCallback(
        (newPage, newPageSize) => setPaginationState(prev => ({ ...prev, pageSize: newPageSize, current: newPage })),
        [setPaginationState],
      ),
      showSizeChanger: true,
      showTotal: useCallback(
        (currentTotal, range) => `${range[0]}-${range[1]} of ${currentTotal} ${pluralEntityName ?? 'Inventory meters'}`,
        [pluralEntityName],
      ),
    } satisfies TablePaginationShape['paginationOptions'],
    setPaginationState,
    resetPaginationState,
  };
};

/**
 * Fixed version of `useFilterWithPagination` hook.
 *
 * Example usage:
 * @example
 * const states = filterWithPaginationStates({
 *  domainKey: STATE_KEY,
 *  filterDefaultValue: DEFAULT_FILTER,
 * });
 *
 * export const useTabFilterWithPagination = (): ShoppingCartTabFilterWithPaginationShape =>
 *  useFilterWithPaginationV2(states);
 */
export const useFilterWithPaginationV2 = <TFilter, TPagination extends DefaultPagination = DefaultPagination>(
  states: FilterWithPaginationStates<TFilter, TPagination>,
  pluralEntityName?: string,
): FilterWithPaginationStateShape<TFilter> => {
  const { pagination, setPaginationState, resetPaginationState } = usePagination(states.pagination, pluralEntityName);
  const { filter, setFilterState, resetFilterState } = useFilter(states.filter);

  const setFilterAndPaginationStateCallback = useCallback<
    FilterWithPaginationStateShape<TFilter>['setFilterAndPaginationState']
  >(
    (changedFilter: Partial<TFilter>, changedPagination: Partial<DefaultPagination>) => {
      setFilterState(prevState => ({ ...prevState, ...changedFilter }));
      setPaginationState(prevState => ({ ...prevState, ...changedPagination }));
    },
    [setFilterState, setPaginationState],
  );

  const setFilterStateCallback = useCallback<FilterWithPaginationStateShape<TFilter>['setFilterState']>(
    changedFilter => {
      setFilterState(prevState => ({ ...prevState, ...changedFilter }));
    },
    [setFilterState],
  );

  const setPaginationStateCallback = useCallback<FilterWithPaginationStateShape<TFilter>['setPaginationState']>(
    changedPagination => {
      setPaginationState(prevState => {
        // NOTE: this code changes page when user starts deleting rows and total count is decreased
        let current = changedPagination.current ?? prevState.current;
        const pageSize = changedPagination.pageSize ?? prevState.pageSize;
        if (
          changedPagination.total !== undefined &&
          current > 1 &&
          (current - 1) * pageSize >= changedPagination.total
        ) {
          current -= 1;
        }
        return { ...prevState, ...changedPagination, current };
      });
    },
    [setPaginationState],
  );

  return {
    filter,
    pagination,
    resetFilterState,
    resetPaginationState,
    setFilterState: setFilterStateCallback,
    setPaginationState: setPaginationStateCallback,
    setFilterAndPaginationState: setFilterAndPaginationStateCallback,
  };
};
